import React from "react";
import { NavLink, useParams } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Flex,
  Grid,
  VStack,
  Tag,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  TagLabel,
  TableContainer,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { useAppContext } from "../../../contexts/AppContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ChevronLeft, Currency, Earth, Wrench, PieChart } from "lucide-react";
import InvestmentDocumentsTable from "./InvestmentDocumentsTable";
import useCustomTheme from "../../../hooks/useCustomTheme";

const InvestmentDetail = () => {
  const { fundName, investmentName } = useParams();
  const { data, formatMillions, tableSize, formatDate } = useAppContext();
  const { theme } = useCustomTheme();

  // Format the fund name to match the URL and find the fund
  const formattedFundName = fundName.replace(/-/g, " ");
  const fund = data.find((fund) => fund.fundName === formattedFundName);

  if (!fund) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="lg">Fund Not Found</Heading>
        <Text>The requested fund does not exist in our database.</Text>
      </Box>
    );
  }

  // Find the specific investment
  const investment = fund.investmentsTable.find(
    (inv) => inv.investmentName.replace(/ /g, "-") === investmentName
  );

  if (!investment) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="lg">Investment Not Found</Heading>
        <Text>The requested investment does not exist in our database.</Text>
      </Box>
    );
  }

  const cashFlowData = investment.cashFlows.map((flow) => ({
    date: flow.date,
    contribution: flow.contribution,
    distribution: flow.distribution,
  }));

  return (
    <Flex direction="column" gap="8">
    
    <Flex justify={{ lg: "space-between", base: "flex-start" }} w={"full"} align={"center"} direction={{ lg: "row", base: "column" }} gap={4}>
            
            <Flex align={"center"} gap={4}>
              <IconButton
                aria-label="Back"
                variant={"ghost"}
                icon={<ChevronLeft  />}
                as={NavLink}
                to={`/funds/${fund.fundName}#investments`}
                size={{ lg: "md", base: "xs" }}
              />
              <h1>{investment.investmentName}</h1>
              <Tag gap={2} size={"lg"}>
                <Box
                  as="span"
                  bg={fund.investmentStrategy.color}
                  h="8px"
                  w="8px"
                  borderRadius={"full"}
                />
                <TagLabel>{investment.type}</TagLabel>
              </Tag>
            </Flex>
        
        
        <Flex direction={"row"} gap={8}>
          <Tooltip label="Geography">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <Earth size={16} color={theme.colors.grey[500]} />
              {investment.geographicLocation}
            </Flex>
          </Tooltip>

          <Tooltip label="Currency">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <Currency size={16} color={theme.colors.grey[500]} />
              {fund.fundCurrency.ISO}
            </Flex>
          </Tooltip>

          <Tooltip label="GICS">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <Wrench size={16} color={theme.colors.grey[500]} />
              {investment.GICSSector}
            </Flex>
          </Tooltip>

          <Tooltip label="Ownership Percentage">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <PieChart size={16} color={theme.colors.grey[500]} />
              {investment.ownershipPercentage}%
            </Flex>
          </Tooltip>


        </Flex>
    </Flex>




    <Tabs variant="line" colorScheme="brand">
        <TabList mb={8}>
          <Tab>Overview</Tab>
          <Tab>Valuation History</Tab>
          <Tab>Documents</Tab>
        </TabList>

        <TabPanels>
        
        <TabPanel  p={0}>

    <Grid gap={8} direction="column" templateColumns={"repeat(12, 1fr)"}>
    
    <Flex gridColumn={{ lg: "span 8", base: "span 12" }} direction="column" gap={16}>      



    <VStack
        spacing={6}
        justifyContent={"space-between"}
        alignItems={"space-between"}>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}
        sx={{ 
            ".chakra-stat": { 
                border: "1px solid", 
                borderColor: "grey.200",
                p: 4,
                borderRadius: 8
            }
            }}   >
          <Stat>
            <StatLabel>IRR</StatLabel>
            <StatNumber>{investment.irr || "N/A"}%</StatNumber>
            <StatHelpText>Internal Rate of Return</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>MoIC</StatLabel>
            <StatNumber>{investment.moc}x</StatNumber>
            <StatHelpText>Multiple on Invested Capital</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Realized Gains</StatLabel>
            <StatNumber>
              {fund.fundCurrency.ISO}{" "}
              {formatMillions(investment.realizedGains)}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Unrealized Gains</StatLabel>
            <StatNumber>
              {fund.fundCurrency.ISO}{" "}
              {formatMillions(investment.unrealizedGains)}
            </StatNumber>
          </Stat>
          <Stat>
              <StatLabel>Investment Value</StatLabel>
              <StatNumber>
                {fund.fundCurrency.ISO}{" "}
                {formatMillions(investment.investmentValue)}
              </StatNumber>
              <StatHelpText>Initial Investment</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Fair Market Value</StatLabel>
              <StatNumber>
                {fund.fundCurrency.ISO}{" "}
                {formatMillions(investment.fairMarketValue)}
              </StatNumber>
              <StatHelpText>As of latest valuation</StatHelpText>
            </Stat>
           
        </SimpleGrid>
      </VStack>


      <Flex gap={4} direction={"column"}>
        <h5>Cash Flow Analysis</h5>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={cashFlowData}>
            <CartesianGrid strokeDasharray="1 1" />
            <XAxis dataKey="date" />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="contribution"
              stroke="#8884d8"
              name="Contributions"
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="distribution"
              stroke="#82ca9d"
              name="Distributions"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Flex>
      


      </Flex>


      <Flex gridColumn={{ lg: "span 4", base: "span 12" }} direction={"column"} gap={16} borderLeft={{ lg: "1px solid", base: "none" }} borderColor={{ lg: "grey.200", base: "none" }} pl={{ lg: 8, base: 0 }}>


        <Box>
          <VStack align="start" spacing={4}>


          <Stat>
              <StatLabel>Investment date</StatLabel>
              <StatNumber>
                {formatDate(investment.investmentDate)}
              </StatNumber>
            </Stat>

            <Stat>
              <StatLabel>Investment phase</StatLabel>
              <StatNumber color={investment.phase === "Invested" ? "green" : "orange"}>
                {investment.phase}
              </StatNumber>
            </Stat>

            <Stat>
              <StatLabel>Deal stage</StatLabel>
              <StatNumber>
              {investment.dealStage}
              </StatNumber>
            </Stat>

            <Stat>
              <StatLabel>ESG Classification</StatLabel>
              <StatNumber>
              {investment.ESGClassification}
              </StatNumber>
            </Stat>
            
     

          

            
      
          </VStack>
        </Box>

       


    
    </Flex>
      </Grid>

            </TabPanel>


            <TabPanel p={0}>
                    <TableContainer>
        <Table variant="simple" size={tableSize}>
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Valuation Method</Th>
              <Th isNumeric>Valuation</Th>
              <Th>Committee Notes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {investment.valuationHistory.map((valuation, index) => (
              <Tr key={index}>
                <Td><Badge>{formatDate(valuation.date)}</Badge></Td>
                <Td>{valuation.method}</Td>
                <Td isNumeric>
                  {fund.fundCurrency.ISO} {formatMillions(valuation.amount)}
                </Td>
                <Td>{valuation.notes}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table></TableContainer>

            </TabPanel>


            <TabPanel  p={0}>

       <InvestmentDocumentsTable investment={investment} />

            </TabPanel>


        </TabPanels>

      </Tabs>

      

     

     

     

    </Flex>
  );
};

export default InvestmentDetail;
